import React, { ReactElement, useMemo, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { TaskListFormDrawer } from "src/components";
import {
    QualityHubListsViewProps,
    QualityHubListsViewState,
    QualityHubListsViewStateProps,
} from "./QualityHubListsView.types";
import { PageHeader, ActionMenu } from "src/ui";
import { IContentHeaderButton } from "src/ui/components/PageHeader";
import { localize } from "src/l10n";
import { DataList, DataListColumn, DataListFetchHandler, CreateButtonProps } from "src/components/DataList";
import { queryTaskListsAsync } from "src/api";
import { Link, useHistory } from "react-router-dom";
import SpintrList from "src/ui/components/SpintrList/SpintrList";
import { Conditional } from "src/components/Conditional";
import { SpintrTypes } from "src/typings";

/**
 * Renders the lists view of the Quality Hub.
 */
function QualityHubListsView({
    hidePageHeader = false,
}: QualityHubListsViewProps): ReactElement {
    const history = useHistory();
    const listRef = useRef<SpintrList>(null);
    const [state, setState] = useState<QualityHubListsViewState>({
        drawerOpen: false,
    });

    const {
        canManageLists,
        canCustomizeLists,
    } = useSelector<Spintr.AppState, QualityHubListsViewStateProps>(
        (appState) => {
            const isAdmin = appState.profile.active.roles.includes("administrators");

            return {
                canManageLists: isAdmin || (appState.privileges.data || [])
                    .some((p: any) => p.value && p.privilegeId === SpintrTypes.Privilege.IsQualityHubAdministrator),

                canCustomizeLists:
                    appState.profile.active.leadsDepartment ||
                    appState.profile.active.leadsOffice ||
                    appState.profile.active.leadsCompany
            };
        },
    );

    const buttons = useMemo<IContentHeaderButton[]>(() => [
        { className: "commandBarAddButton commandBarButton primaryBGColor primaryTextContrast"
        , iconProps: { iconName: "Add" }
        , key: "create-button"
        , onClick: () => setState((s) => ({ ...s, drawerOpen: true }))
        , text: localize("SkapaNy")
        , theme: "primary"
        }
    ], [setState]);

    const createButton = useMemo<CreateButtonProps>(() => ({
        onClick: () => setState((s) => ({ ...s, drawerOpen: true })),
    }), []);

    const onDrawerDismissed = useCallback((saved: boolean) => {
        setState((s) => ({ ...s, drawerOpen: false }));
        if (!saved) {
            return;
        }

        listRef?.current?.reFetch?.();
    }, [listRef, setState]);

    const l10n = useMemo(() => ({
        lists: localize("QH_LISTS"),
    }), []);

    const columns = useMemo<DataListColumn[]>(() => [{
        fieldName: "name",
        isSortable: false,
        key: "name",
        minWidth: 0,
        name: localize("Namn"),
        onRender: (item) => {
            if (canManageLists) {
                return (
                    <Link to={`/quality-hub/${item.id}/tasks`}>
                        {item.name}
                    </Link>
                );
            } else if (canCustomizeLists && item.allowOverride) {
                return (
                    <Link to={`/quality-hub/${item.id}/customize`}>
                        {item.name}
                    </Link>
                );
            } else {
                return <span>{item.name}</span>;
            }
        },
    }, {
        key: "actions",
        name: "",
        maxWidth: 24,
        minWidth: 24,
        onRender: (item) => {
            const items: Spintr.IActionMenuItem[] = [];

            if (canManageLists) {
                items.push({
                    text: localize("Redigera"),
                    onClick: () => setState((prevState) => ({
                        ...prevState,
                        drawerOpen: true,
                        selectedId: item.id,
                    })),
                });
            }

            if (canCustomizeLists && item.allowOverride) {
                items.push({
                    text: localize("Anpassa"),
                    onClick: () => history.push(
                        `/quality-hub/${item.id}/customize`,
                    ),
                });
            }

            if (items.length === 0) {
                return null;
            }

            return <ActionMenu categories={[{ items, }]} />;
        }
    }], [history, setState, canManageLists, canCustomizeLists]);

    const fetchGlobalLists = useCallback<DataListFetchHandler>(
        (_, __, ___, ____, _____, cancelToken) => queryTaskListsAsync(
            cancelToken,
        ).then((envelope) => ({
            data: envelope.items,
            total: envelope.totalCount,
        })), []);

    return (
        <div id="QualityHubListsView">
            <Conditional condition={!hidePageHeader}>
                <PageHeader buttons={buttons}
                            displaySearch={false}
                            title={l10n.lists}/>
            </Conditional>
            <DataList columns={columns}
                      createButton={hidePageHeader ? createButton : undefined}
                      fetch={fetchGlobalLists}
                      orderByColumn="name"
                      ref={listRef} />
            <TaskListFormDrawer onDismiss={onDrawerDismissed}
                                open={state.drawerOpen}
                                taskListId={state.selectedId} />
        </div>
    );
}

export default QualityHubListsView;
